import type { AppType } from './questions';

/**
 * The ads' demo sites, shown live on the first screen of an ad visit (2026-10-04,
 * landing_pages/docs/preview-first-journey-handoff.md v2, step 1). From
 * landing_pages/saddle-ads/launch/2026-10/ads.csv: each ad links here with its demo's sentence as
 * `?idea=` and its name as `utm_content` (`Golf-W1-A`).
 *
 * These are PROTECTED slugs: shown, never modified, unlisted or republished (docs/PROTECTED-SLUGS.md).
 * How a demo is built (on the best model), checked and kept current: docs/DEMOS.md.
 * Only these may be framed by the builder (site.ts PREVIEW_DEMO_SLUGS keeps the same list, plus the
 * slugs they replaced).
 *
 * Wedding, Volunteer and Golf were rebuilt on 2026-10-12 from fuller prompts on Fable 5.1 ("blow
 * them away with the demo"): `madeFrom` is that prompt, and "Make one like this for you" builds from
 * it, so the visitor's build aims at the app they just looked at. `sentence` stays the ad's own, which
 * the ads still link with.
 */
export interface AdDemo {
  /** The ad family, as utm_content starts: `Golf` in `Golf-W1-A`. */
  ad: string;
  slug: string;
  /** The sentence the ad links with (`?idea=`). */
  sentence: string;
  /** The prompt the demo was built from, when it is not `sentence`. */
  madeFrom?: string;
  /** V7: the demo screen's full-width button ("Build your own golf tracker"). */
  cta: string;
  /** V7: what "What is it?" starts on for this ad (journey/questions.ts): Golf → golf, Wedding → rsvp… */
  type: AppType;
  /** V6: a still of the demo (web/public/demos/<slug>.jpg), shown until the live one has loaded. */
  poster: string;
  /** Uses AI at runtime (the flagship examples, 2026-11-02): listed first on /new/start, with a "Uses AI" badge. */
  ai?: boolean;
  /** The card's title on /new/start, when the default for its ad does not fit. */
  title?: string;
  /** Its value for `starter_chosen` (analytics/funnel.ts), when it is not one of the original six. */
  starter?: string;
}

/** What "Make one like this for you" builds: the prompt the demo itself came from. */
export const buildsFrom = (demo: AdDemo): string => demo.madeFrom ?? demo.sentence;

const WEDDING_MADE_FROM = "A wedding website for Mia and Theo's weekend in Sonoma, June 11-13, 2027, that looks like an elegant editorial wedding magazine: a cover with their names, the date and a countdown, their story, the weekend's schedule day by day with times and places, where to stay with booking links, travel tips, and an RSVP where each guest says which events they are coming to, picks their meal, notes any dietary needs and can add a plus-one, then sees a warm confirmation.";
const TRIP_SENTENCE = "A shared itinerary for our group trip to Lisbon, May 14-19, 2027: each day planned with times and places, where we are staying, everyone's flights, and a packing list, so the whole group can see the plan on their phones.";
const POTLUCK_SENTENCE = "A potluck sign-up for our street's summer block party on Saturday, July 17, 2027: people say what dish they are bringing (main, side, dessert or drinks) so we do not end up with ten salads, and everyone can see what is already covered.";
const VOLUNTEER_MADE_FROM = "A volunteer scheduler for our food bank that looks polished and friendly. Volunteers see the next four weeks of shifts on a calendar, each with its time, its role and how many people it still needs, pick a shift and sign up with their name and email, and a shift closes when it is full. Each shift shows who has signed up so far, by first name, so the calendar feels busy and alive, and a short guide tells new volunteers what to bring and where to go.";
const GOLF_MADE_FROM = "A golf round tracker that looks and feels like a premium sports app. Before teeing off I take a photo of the course's scorecard and the app reads each hole's par and yardage, so the round is set up for me. During the round I log every stroke hole by hole with big thumb-friendly buttons: the club, roughly how far it went, and where it ended up (fairway, green, rough, bunker or water). After the round, and across all my rounds, it shows rich analytics: scoring average and its trend, score by par 3, 4 and 5, fairways hit, greens in regulation, putts per round, and for every club the average and longest distance, how often it is a good shot, and which club costs me the most strokes, all with clean charts.";
const CAFE_SENTENCE = "Staff schedule for my café. Every Sunday night I post next week's shifts: open 6:30 to 12, mid 11 to 4, close 2 to 7, two people each. Staff pick their shifts with their first name, and a full shift stops taking sign-ups.";

const AI_LISTING_MADE_FROM = "A \"snap and sell\" studio for people selling things online (Marketplace, eBay, Etsy), that looks like a clean, premium product-photo studio app. On the first screen, you take a photo of an item or upload one (up to 3 photos). The AI looks at the photos and writes a listing: a title, a 3-sentence description, the condition, 5 search tags, and a suggested price range with a one-line reason. Every field can be edited and copied with one tap. Then \"Studio shots\" makes two new photos of the SAME item from the user's own photo: one on a seamless white background, and one in a styled lifestyle scene the user picks (a cosy living room, a sunlit kitchen, a minimal studio, or outdoors). Before and after are shown side by side, and each can be downloaded. Every AI button shows its credit cost. Past listings are kept on the phone in a \"My listings\" tab, with their photos. Include one finished sample listing (a vintage leather camera bag) with its studio shots, so the first screen is never empty. This is a public showcase whose AI the owner has opened to visitors (S.ai.open is true): a visitor who is not signed in can use every AI feature straight away, and their results are kept on their phone (S.store), with a small 'Demo: kept on this phone' note. Offer signing in only to keep things across devices, never before trying. Show the credit cost on every AI button before it is tapped.";
const AI_ROOM_MADE_FROM = "An interior design \"room makeover\" app that looks like a high-end interiors magazine. The user photographs a room (or uploads one) and picks a style from visual chips: Japandi, Mid-century, Coastal, Industrial, Cottagecore, Minimal. The AI redesigns the SAME room in that style, keeping its walls, windows and layout, and shows a before/after slider. Under it, the AI lists 6 to 8 pieces to get the look (item, why it works, a rough price band). There's also a one-line \"designer's note\" on what changed and why. The user can try another style on the same photo. Saved makeovers live in a \"My rooms\" gallery on the phone. Every AI button shows its credit cost. Include one finished sample (a plain rental living room made over in Japandi) so the first screen shows the magic straight away. This is a public showcase whose AI the owner has opened to visitors (S.ai.open is true): a visitor who is not signed in can use every AI feature straight away, and their results are kept on their phone (S.store), with a small 'Demo: kept on this phone' note. Offer signing in only to keep things across devices, never before trying. Show the credit cost on every AI button before it is tapped.";
const AI_VOICE_MADE_FROM = "A voice-notes app for busy people that looks calm and focused, like a premium journaling app. Its big record button records in the page (with a live timer and a stop button, up to 3 minutes). When the recording stops, the AI transcribes it and turns it into a clean note: a title, a 2-sentence summary, key points, and action items as checkboxes, each with an optional due date it picked up from what was said. Notes are listed newest first, with search. Each note can be read aloud with a natural voice, the transcript copied, and action items ticked off. The user can also upload an existing audio file. Every AI step shows its credit cost before it runs. Include two sample notes (a \"planning the team offsite\" ramble, and a \"groceries and errands\" list) so it is never empty. This is a public showcase whose AI the owner has opened to visitors (S.ai.open is true): a visitor who is not signed in can use every AI feature straight away, and their results are kept on their phone (S.store), with a small 'Demo: kept on this phone' note. Offer signing in only to keep things across devices, never before trying. Show the credit cost on every AI button before it is tapped.";
const AI_STORY_MADE_FROM = "A magical storybook maker for parents and kids, which looks like a beautifully illustrated children's book with warm paper textures and a friendly, playful font for headings. A parent photographs their child's drawing (or uploads it) and types the hero's name and one idea (\"goes to the moon\", \"finds a lost puppy\"). The AI looks at the drawing and writes a 5-page story starring that character, 2\u20133 sentences per page, gentle and funny, for ages 3\u20137. Each page gets an illustration in a soft picture-book style that keeps the character looking like the child's drawing (the drawing is the reference). Pages turn like a book. A \"Read it to me\" button narrates each page in a warm voice. Finished books are kept on a \"Our books\" shelf on the phone. Every AI step shows its credit cost before it runs. Include one finished sample book (\"Pip the Purple Dragon finds the rainbow\") so the shelf is never empty. This is a public showcase whose AI the owner has opened to visitors (S.ai.open is true): a visitor who is not signed in can use every AI feature straight away, and their results are kept on their phone (S.store), with a small 'Demo: kept on this phone' note. Offer signing in only to keep things across devices, never before trying. Show the credit cost on every AI button before it is tapped.";

export const AD_DEMOS: AdDemo[] = [
  {
    ad: 'Wedding',
    slug: 'mia-theo-in-sonoma',
    sentence: "A wedding website for Mia and Theo's weekend in Sonoma, June 11-13, 2027: the schedule for each day, where to stay, and an RSVP where each guest says if they're coming, picks their meal and can add a plus-one.",
    madeFrom: WEDDING_MADE_FROM,
    cta: 'Build your own wedding website',
    type: 'rsvp',
    poster: '/demos/mia-theo-in-sonoma.jpg',
  },
  {
    ad: 'Trip',
    slug: 'lisbon-2027-the-plan',
    sentence: TRIP_SENTENCE,
    cta: 'Build your own trip plan',
    type: 'other',
    poster: '/demos/lisbon-2027-the-plan.jpg',
  },
  {
    ad: 'Potluck',
    slug: 'block-party-potluck-2',
    sentence: POTLUCK_SENTENCE,
    cta: 'Build your own potluck sign-up',
    type: 'other',
    poster: '/demos/block-party-potluck-2.jpg',
  },
  {
    ad: 'Volunteer',
    slug: 'pantry-shift-board',
    sentence: "A volunteer sign-up for our food bank: a calendar of the next four weeks of shifts, each with a time and how many volunteers it needs; volunteers pick a shift and sign up, and a shift closes when it's full.",
    madeFrom: VOLUNTEER_MADE_FROM,
    cta: 'Build your own volunteer schedule',
    type: 'scheduler',
    poster: '/demos/pantry-shift-board.jpg',
  },
  {
    ad: 'Golf',
    slug: 'fairway-ledger-2',
    sentence: 'A golf tracker where I log each round with my score on every hole and the club I used for each stroke, and see over time how far and how well I hit each club, with charts.',
    madeFrom: GOLF_MADE_FROM,
    cta: 'Build your own golf tracker',
    type: 'golf',
    poster: '/demos/fairway-ledger-2.jpg',
  },
  {
    // A café rota for the Scheduling ads (schedule-demo-handoff, 2026-10-12), rebuilt on 2026-10-13 from
    // the same sentence as caf-shift-board: that board belongs to another account and drew 6:30 shifts
    // from 13:30 (docs/DEMOS.md).
    ad: 'Schedule',
    slug: 'caf-shift-picker',
    sentence: CAFE_SENTENCE,
    cta: 'Build your own café schedule',
    type: 'scheduler',
    poster: '/demos/caf-shift-picker.jpg',
  },
  {
    // Flagship AI example (2026-11-02, docs/demos/ai-flagships-2026-11.md): its AI is open to signed-out visitors.
    ad: "Snap & sell",
    slug: 'snap-sell-studio',
    sentence: "A snap-and-sell studio: take a photo of something I'm selling and the AI writes the listing \u2014 title, description, condition, tags and a fair price \u2014 then makes studio shots of the same item on a white background and in a styled scene.",
    madeFrom: AI_LISTING_MADE_FROM,
    cta: "Make your own selling studio",
    type: 'other',
    poster: '/demos/snap-sell-studio.jpg',
    ai: true,
    title: "A photo-to-listing studio",
    starter: 'listing',
  },
  {
    // Flagship AI example (2026-11-02, docs/demos/ai-flagships-2026-11.md): its AI is open to signed-out visitors.
    ad: "Room",
    slug: 'room-makeover',
    sentence: "A room makeover app: photograph a room, pick a style like Japandi or Mid-century, and the AI redesigns the same room in that style with a before/after slider and a list of pieces to get the look.",
    madeFrom: AI_ROOM_MADE_FROM,
    cta: "Make your own makeover app",
    type: 'other',
    poster: '/demos/room-makeover.jpg',
    ai: true,
    title: "A room makeover with AI",
    starter: 'room',
  },
  {
    // Flagship AI example (2026-11-02, docs/demos/ai-flagships-2026-11.md): its AI is open to signed-out visitors.
    ad: "Voice",
    slug: 'quiet-notes',
    sentence: "A voice-notes app: record what's on my mind in the page, and the AI turns it into a clean note with a title, a summary and action items, and can read it back to me.",
    madeFrom: AI_VOICE_MADE_FROM,
    cta: "Make your own voice-notes app",
    type: 'other',
    poster: '/demos/quiet-notes.jpg',
    ai: true,
    title: "Voice notes that write themselves",
    starter: 'voice',
  },
  {
    // Flagship AI example (2026-11-02, docs/demos/ai-flagships-2026-11.md): its AI is open to signed-out visitors.
    ad: "Story",
    slug: 'our-storybook',
    sentence: "A storybook maker: photograph my child's drawing, give the hero a name and an idea, and the AI writes a five-page story starring it, illustrates every page in the drawing's likeness and reads it aloud.",
    madeFrom: AI_STORY_MADE_FROM,
    cta: "Make your own storybook maker",
    type: 'other',
    poster: '/demos/our-storybook.jpg',
    ai: true,
    title: "A storybook from a child's drawing",
    starter: 'story',
  },
];

export const SITE_ORIGIN = 'https://app.saddle-sites.com';

/** The demo an ad visit came for: by the ad's name first, then by the exact sentence it linked. */
export function demoForVisit(utmContent: string | undefined, idea: string | undefined): AdDemo | null {
  // The link's own sentence first (2026-11): a creator's utm_content is their handle, and "golf-mom"
  // linking the volunteer demo must get the volunteer demo. An ad's sentence and its name always agree.
  const said = (idea ?? '').trim();
  const bySentence = said ? AD_DEMOS.find((d) => d.sentence === said) : undefined;
  if (bySentence) return bySentence;
  const family = (utmContent ?? '').split('-')[0]?.toLowerCase();
  return (family ? AD_DEMOS.find((d) => d.ad.toLowerCase() === family) : undefined) ?? null;
}

/**
 * The demo a build's idea came from: its ad's sentence, the prompt the demo was made from, or V7's
 * prompt — the ad's sentence, unedited, with the answers' lines under it (journey/questions.ts).
 * Null for anybody's own words.
 */
export function demoForIdea(idea: string): AdDemo | null {
  const said = idea.trim();
  if (!said) return null;
  return AD_DEMOS.find((d) => said === d.sentence || said === buildsFrom(d) || said.startsWith(`${d.sentence}\n`)) ?? null;
}

/**
 * What the "it's ready" email's button calls the app, "Open my <noun>" (V7, POST /waiting/ready-link
 * `thing`): the ad's own words for the four ad types the server knows, else "app". By the build's
 * idea first (the ad's sentence with the answers under it), else by the ad it began from — while
 * "What is it?" was left on that ad's type.
 */
const EMAIL_NOUNS: Record<string, string> = {
  Golf: 'golf tracker',
  Wedding: 'wedding website',
  Volunteer: 'volunteer schedule',
  Schedule: 'café schedule',
};
export function emailNoun(goal: string, began?: { demo: string | null; type: AppType }): string {
  const fromGoal = demoForIdea(goal);
  const fromAd = began?.demo ? AD_DEMOS.find((d) => d.slug === began.demo && d.type === began.type) : undefined;
  const demo = fromGoal ?? fromAd;
  return (demo && EMAIL_NOUNS[demo.ad]) || 'app';
}
